import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface RollReviewSettings {
  accept_roll_reviews: boolean;
}

const QUERY_KEY = (userId: string) => ['roll-review-settings', userId];

export const useRollReviewSettings = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<RollReviewSettings>({
    queryKey: QUERY_KEY(user?.id ?? ''),
    queryFn: async () => {
      if (!user) throw new Error('No user');
      const { data, error } = await supabase
        .from('profiles')
        .select('accept_roll_reviews')
        .eq('id', user.id)
        .single();
      if (error) throw error;
      return { accept_roll_reviews: data?.accept_roll_reviews ?? true };
    },
    enabled: !!user,
  });

  const mutation = useMutation({
    mutationFn: async (value: boolean) => {
      if (!user) throw new Error('No user');
      const { error } = await supabase
        .from('profiles')
        .update({ accept_roll_reviews: value })
        .eq('id', user.id);
      if (error) throw error;
    },
    onMutate: async (value) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: QUERY_KEY(user?.id ?? '') });
      const prev = queryClient.getQueryData<RollReviewSettings>(QUERY_KEY(user?.id ?? ''));
      queryClient.setQueryData<RollReviewSettings>(QUERY_KEY(user?.id ?? ''), {
        accept_roll_reviews: value,
      });
      return { prev };
    },
    onError: (_err, _val, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(QUERY_KEY(user?.id ?? ''), ctx.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(user?.id ?? '') });
    },
  });

  return {
    acceptRollReviews: data?.accept_roll_reviews ?? true,
    isLoading,
    setAcceptRollReviews: mutation.mutate,
    isUpdating: mutation.isPending,
  };
};
